import type { AxiosPromise } from 'axios';
import { ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import type { TcrListParams } from '~/services/adminTenDlcClient.types';
import type { CursorPaginationResponse } from '~/services/api';
import { download, prepareCsv } from '~/utils/files';
import { catchRequestError } from '~/utils/request';

const EXPORT_PER_PAGE = 250;

export interface TenDlcExportField<T> {
    header: string;
    value: (row: T) => string | number;
}

export const useTenDlcExport = <T>(
    fetchPage: (params: TcrListParams) => AxiosPromise<CursorPaginationResponse<T>>,
    fields: TenDlcExportField<T>[],
    fileName: string
) => {
    const toasted = useToasted();
    const exporting = ref(false);

    const exportCsv = async (params: TcrListParams) => {
        if (exporting.value) {
            return;
        }

        exporting.value = true;

        // Copied up front because the sidebar edits the filters in place.
        const filters = { ...params, per_page: EXPORT_PER_PAGE };
        const rows: T[] = [];
        let cursor: string | null = null;

        try {
            do {
                const { data } = await fetchPage({ ...filters, cursor });

                rows.push(...data.data);
                cursor = data.next_page_url
                    ? new URLSearchParams(data.next_page_url.split('?')[1]).get('cursor')
                    : null;
            } while (cursor);

            download(
                prepareCsv(
                    fields.map((field) => field.header),
                    rows.map((row) => fields.map((field) => field.value(row)))
                ),
                fileName,
                { type: 'text/csv;charset=utf-8;' }
            );
        } catch (error) {
            toasted.error(catchRequestError(error));
        } finally {
            exporting.value = false;
        }
    };

    return { exporting, exportCsv };
};
